// Минимальный клиентский роутер на History API — без зависимостей.
// navigate() меняет URL и оповещает подписчиков; useRoute() парсит pathname.

import { useEffect, useState } from 'react'

export type Route =
  | { name: 'dashboard' }
  | { name: 'project'; projectId: string }
  | { name: 'post'; postId: string }
  | { name: 'invite'; projectId: string }
  | { name: 'notFound' }

const NAV_EVENT = 'mrktng:navigate'

// state — необязательное состояние истории (напр. { peekProjectId } для peek-панели поста).
// Если путь не меняется, но передан state — переписываем его через replaceState
// (так кнопка «открыть страницей» очищает peek, оставаясь на том же /posts/:id).
export function navigate(path: string, state?: unknown): void {
  if (path !== window.location.pathname + window.location.search) {
    window.history.pushState(state ?? null, '', path)
  } else if (state !== undefined) {
    window.history.replaceState(state, '', path)
  }
  window.dispatchEvent(new Event(NAV_EVENT))
}

export function parseRoute(pathname: string): Route {
  const segments = pathname.replace(/\/+$/, '').split('/').filter(Boolean)
  if (segments.length === 0) {
    return { name: 'dashboard' }
  }
  if (segments[0] === 'projects' && segments[1]) {
    return { name: 'project', projectId: segments[1] }
  }
  if (segments[0] === 'posts' && segments[1]) {
    return { name: 'post', postId: segments[1] }
  }
  if (segments[0] === 'invites' && segments[1]) {
    return { name: 'invite', projectId: segments[1] }
  }
  return { name: 'notFound' }
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname))

  useEffect(() => {
    const update = () => setRoute(parseRoute(window.location.pathname))
    window.addEventListener('popstate', update)
    window.addEventListener(NAV_EVENT, update)
    return () => {
      window.removeEventListener('popstate', update)
      window.removeEventListener(NAV_EVENT, update)
    }
  }, [])

  return route
}

// Ссылка, перехватывающая клик для SPA-навигации (обычный клик — без перезагрузки).
export function Link(props: {
  href: string
  className?: string
  children: React.ReactNode
  title?: string
}): React.ReactElement {
  const onClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
      return
    }
    event.preventDefault()
    navigate(props.href)
  }
  return (
    <a href={props.href} className={props.className} title={props.title} onClick={onClick}>
      {props.children}
    </a>
  )
}
